import React, { useEffect, useRef, useState } from 'react';
import { product, launch, brand } from '../mock/data';
import { RotateCw, Bell } from 'lucide-react';
import { useNotify } from './Notify';
import Countdown from './Countdown';

const Hero = () => {
  const videoRef = useRef(null);
  const { open } = useNotify();
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(rm.matches);
    update();
    rm.addEventListener('change', update);
    return () => rm.removeEventListener('change', update);
  }, []);

  // Robust continuous spin (auto-play loop). Retry on interaction if autoplay is blocked.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (reduced) {
      v.pause();
      return;
    }

    v.loop = true;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    if (v.readyState >= 2) tryPlay();
    v.addEventListener('loadeddata', tryPlay, { once: true });
    v.addEventListener('canplay', tryPlay, { once: true });

    const onInteract = () => tryPlay();
    window.addEventListener('pointerdown', onInteract, { once: true });
    window.addEventListener('scroll', onInteract, { once: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('scroll', onInteract);
    };
  }, [reduced]);

  return (
    <section id="top" className="relative bg-white min-h-screen w-full flex flex-col items-center overflow-hidden">
      {/* soft stage glow behind product */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(58% 46% at 50% 56%, rgba(124,58,237,0.06), transparent 72%)' }}
      />

      {/* 360 badge (right side, clear of the header text; hidden on mobile) */}
      <div className="absolute top-1/2 -translate-y-1/2 right-6 lg:right-12 pill px-3 py-1.5 z-20 max-md:!hidden">
        <RotateCw size={13} className="text-[var(--accent)] animate-spin" style={{ animationDuration: '4s' }} />
        <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">360° view</span>
      </div>

      {/* Top: eyebrow + title (above the product, not over it) */}
      <div className="relative z-10 pt-[124px] md:pt-[120px] flex flex-col items-center text-center container-px">
        <div className="pill px-3.5 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
          <span className="text-[11px] uppercase tracking-kicker text-[var(--ink-soft)]">
            {product.edition} · 20 per color
          </span>
        </div>
        <h1 className="mt-5 font-display uppercase font-tight leading-[0.9] text-[clamp(2.4rem,6vw,5rem)]">
          The Lotus <span className="text-[var(--accent)]">Tote</span>
        </h1>
        <p className="mt-2 text-[var(--muted)] text-lg font-serif-i">Sixteen years, made to carry.</p>
      </div>

      {/* Middle: full uncropped spinning product */}
      <div className="relative z-0 flex-1 w-full min-h-0 flex items-center justify-center py-3">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster-v3.jpg"
          className="h-full max-h-[56vh] w-auto max-w-[92vw] object-contain"
        >
          <source src="/hero-v3.webm" type="video/webm" />
          <source src="/hero-v3.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Bottom: Halloween drop + two dates + countdown + notify */}
      <div className="relative z-10 pb-9 md:pb-12 flex flex-col items-center text-center container-px">
        <div className="pill px-4 py-1.5 mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
          <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
            Coming soon · Halloween drop
          </span>
        </div>

        <p className="font-display uppercase font-tight text-[clamp(1.5rem,3.8vw,2.6rem)] leading-[1.05] max-w-[700px]">
          Spend <span className="text-[var(--accent)]">$200</span>, get a free tote
        </p>
        <p className="mt-1.5 text-[12px] uppercase tracking-[0.18em] text-[var(--muted)]">
          Limited edition · a $25 value
        </p>

        <Countdown className="mt-6" />

        <div className="mt-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-[11px] uppercase tracking-[0.14em]">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1.5 text-[var(--ink-soft)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            {launch.phase1}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1.5 text-[var(--muted)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--muted-2)]" />
            {launch.phase2}
          </span>
        </div>

        <button
          data-testid="hero-notify-button"
          onClick={() => open('hero')}
          className="btn-primary mt-6 inline-flex items-center gap-2 px-10 py-4 text-[13px] uppercase tracking-[0.14em]"
        >
          <Bell size={15} />
          Notify me when it drops
        </button>
      </div>
    </section>
  );
};

export default Hero;
